import {
    EventDrugsDto,
    EventFirearmDto,
    EventOtherItemDto,
    EventPanelFormType,
    EventPersonDto,
    EventVehicleDto,
} from '@mark43/cad-api';
import { toJS } from 'mobx';
import {
    LocallyRemovedEventPanelFormFields,
    LocallyRemovedEventPanelFormValues,
    LocallyRemovedFieldKeyType,
} from './locally-removed-fields-store';
import { FormState } from 'react-hook-form/dist/types/form';

type EventFormEntityBase = {
    id?: number | null;
    contextId?: number | null;
    isPersisted: boolean | null;
};

function getEntityId(entity: EventFormEntityBase): number {
    const e = entity;
    if ('id' in e) {
        return e.id ?? -1;
    }
    if ('contextId' in e) {
        return entity.contextId ?? -1;
    }
    return -1;
}

/**
 * keepDirtyValues doesn't cover items in an array (sort of), such as vehicles or reportingParties. Here, we merge in our local changes to each
 * relevant field. If we locally removed it without saving, we filter it out from the remote payload. If we added it locally, we add it to the
 * remote data.
 */
export function preserveLocallyChangedFields(
    locallyRemovedFields: LocallyRemovedEventPanelFormFields,
    localFormData: LocallyRemovedEventPanelFormValues,
    remoteFormData: LocallyRemovedEventPanelFormValues,
    formState: FormState<EventPanelFormType>
): LocallyRemovedEventPanelFormValues {
    const locallyModifiedFormFields = [
        ...Object.keys(locallyRemovedFields),
    ] as LocallyRemovedFieldKeyType[];
    const removedFields = toJS(locallyRemovedFields);
    const mergedFields: LocallyRemovedEventPanelFormValues = {
        vehicles: [],
        mergedVehicles: [],
        towVehicles: [],
        mergedTowVehicles: [],
        reportingParties: [],
        mergedReportingParties: [],
        involvedPersons: [],
        mergedInvolvedPersons: [],
        firearms: [],
        mergedFirearms: [],
        drugs: [],
        mergedDrugs: [],
        otherItems: [],
        mergedOtherItems: [],
    };

    locallyModifiedFormFields.forEach(function mergeField(field) {
        const locallyRemovedValues = new Set(removedFields[field]);
        const localFieldValue: EventFormEntityBase[] = localFormData[field] ?? [];
        const remoteFieldValue: EventFormEntityBase[] = remoteFormData[field] ?? [];

        const localFieldValueIdMap = new Map(localFieldValue.map((v) => [getEntityId(v), v]));
        const remoteFieldValueIdMap = new Map(remoteFieldValue.map((v) => [getEntityId(v), v]));

        const persistedAndLocallyAddedValues = localFieldValue.filter((localVal) => {
            return (
                !localVal.isPersisted ||
                (remoteFieldValueIdMap.has(getEntityId(localVal)) && localVal.isPersisted)
            );
        });

        const newValuesFromNudge = (remoteFieldValue as Array<
            EventVehicleDto | EventPersonDto | EventFirearmDto | EventDrugsDto | EventOtherItemDto
        >).filter((remoteVal) => {
            return !localFieldValueIdMap.has(getEntityId(remoteVal));
        });

        // It's important to preserve the indices of the array items so the form doesn't get messed up
        const mergedData = [...persistedAndLocallyAddedValues, ...newValuesFromNudge];

        const dataWithLocallyRemovedFields = mergedData.filter(
            (v) => !locallyRemovedValues.has(getEntityId(v))
        );

        // If we have a value both in the nudge and locally, merge in the remote field updates, preserving local changes
        const dataWithRemoteUpdatesMergedIn = dataWithLocallyRemovedFields.map((value, index) => {
            const entityId = getEntityId(value);
            const localVal = localFieldValueIdMap.get(entityId);
            const remoteVal = remoteFieldValueIdMap.get(entityId);

            if (localVal && remoteVal) {
                const dirtyFieldsForValue = formState.dirtyFields?.[field]?.[index];
                for (const locallyEditedField in dirtyFieldsForValue ?? []) {
                    delete remoteVal[locallyEditedField as keyof EventFormEntityBase];
                }

                return {
                    ...localVal,
                    ...remoteVal,
                };
            }

            return value;
        });

        // @ts-expect-error This value is alright.
        mergedFields[field] = dataWithRemoteUpdatesMergedIn;
    });

    return mergedFields;
}
